import * as B from '@blueprintjs/core';
import {ExportToCsv} from 'export-to-csv';
import React from 'react';

import {featureDisplayName} from 'app/components/Omnisearch';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization';
import {calculateAreaInUnit} from 'app/utils/geoJsonUtils';
import {makeNoteUrlFragment, makeProjectDashboardUrl} from 'app/utils/routeUtils';

import {RunProgressBar, RunProgressBarStatus} from './RunProgressBar';
import {RunSummary} from './RunView';
import cs from './styles.module.scss';
import {ApiAgentNote, NoteQueueFilterState, SortDirection, SortField} from './types';
import {countNoteReviewStates, isNoteDismissed, isNoteSaved} from './utils';
interface NoteFilterBarProps {
  filters: NoteQueueFilterState;
  setFilterState: (value: Partial<NoteQueueFilterState>) => void;
  noteQueue: ApiAgentNote[];
  notes: ApiAgentNote[];
  features: ApiFeature[];
  areaUnit: ApiOrganizationAreaUnit;
  csvExportFilename: string;
  selectedNotes: ApiAgentNote[];
  clearSelection: () => void;
  selectAllNotes: () => void;
  bulkSaveNotes: () => void;
  bulkDismissNotes: () => void;
}

// Map status to button icon/text to avoid nested ternaries/generally simplify
const STATUS_META = {
  all: {icon: 'filter', text: 'Status'},
  new: {icon: 'form', text: 'New'},
  saved: {icon: 'tick', text: 'Saved'},
  archived: {icon: 'cross', text: 'Archived'},
} as const;

export const NoteFilterBar: React.FC<NoteFilterBarProps> = ({
  filters,
  setFilterState,
  noteQueue,
  notes,
  features,
  areaUnit,
  csvExportFilename,
  selectedNotes,
  clearSelection,
  selectAllNotes,
  bulkSaveNotes,
  bulkDismissNotes,
}) => {
  const statusMeta = STATUS_META[filters.status];
  const handleReset = React.useCallback(() => {
    setFilterState({
      searchParams: ['featureName', ''],
      status: 'all',
      sortParams: ['featureName', 'asc'],
    });
  }, [setFilterState]);

  const handleExportCSV = React.useCallback(() => {
    if (!noteQueue || noteQueue.length === 0) return;

    const csvExporter = new ExportToCsv({
      showLabels: true,
      useBom: false,
      filename: csvExportFilename,
      headers: [
        'Status',
        'Property name',
        'Observation',
        `Property ${areaUnit === 'areaAcre' ? 'acres' : 'hectares'}`,
        `Observation ${areaUnit === 'areaAcre' ? 'acres' : 'hectares'}`,
        'Note Link',
      ],
    });

    const data = noteQueue.map((note) => {
      const feature = features?.find((f) => f.id === note.featureId);
      const featureName = feature ? featureDisplayName(feature) : 'Unknown';
      const observationText = note.text || '';

      const propertyArea = feature?.geometry
        ? calculateAreaInUnit(feature.geometry, areaUnit)?.toFixed(2) || 'N/A'
        : 'N/A';

      const observationGeometryArea = note.geometry
        ? calculateAreaInUnit(note.geometry, areaUnit)?.toFixed(2) || 'N/A'
        : 'N/A';

      const status = isNoteDismissed(note) ? 'Archived' : isNoteSaved(note) ? 'Saved' : 'New';

      // Generate note link for saved notes to export in CSV
      const noteLink =
        isNoteSaved(note) && note.organizationId && note.projectId
          ? makeProjectDashboardUrl(
              note.organizationId,
              note.projectId,
              'map',
              [note.featureId],
              makeNoteUrlFragment(note.id),
              {includeRoot: true}
            )
          : '';

      return {
        Status: status,
        'Property name': featureName,
        Observation: observationText,
        [`Property ${areaUnit === 'areaAcre' ? 'acres' : 'hectares'}`]: propertyArea,
        [`Observation ${areaUnit === 'areaAcre' ? 'acres' : 'hectares'}`]: observationGeometryArea,
        'Note Link': noteLink,
      };
    });

    csvExporter.generateCsv(data);
  }, [noteQueue, features, areaUnit, csvExportFilename]);

  const [sortField, sortDirection] = filters.sortParams as [SortField, SortDirection];

  const {savedCount, dismissedCount, newCount} = React.useMemo(
    () => countNoteReviewStates(notes),
    [notes]
  );

  return (
    <div className={cs.noteFilterBar}>
      {notes.length > 0 && (
        <div style={{display: 'flex', gap: '1rem', marginBottom: '0.5rem'}}>
          <RunSummary features={features} notes={notes} areaUnit={areaUnit} />
          <div style={{flex: 1}}>
            <RunProgressBar
              savedCount={savedCount}
              dismissedCount={dismissedCount}
              newCount={newCount}
            />
          </div>
        </div>
      )}
      <div className={cs.noteFilterBarControls}>
        <div className={cs.multiTextFilter}>
          <B.InputGroup
            className={cs.searchInput}
            leftIcon="search"
            placeholder={`Filter by ${filters.searchParams[0] === 'featureName' ? 'property name' : 'observation text'}...`}
            value={filters.searchParams[1]}
            onChange={(e) =>
              setFilterState({searchParams: [filters.searchParams[0], e.target.value]})
            }
            rightElement={
              <B.Popover
                content={
                  <B.Menu>
                    <B.MenuItem
                      text="Search property name"
                      icon={filters.searchParams[0] === 'featureName' ? 'small-tick' : 'blank'}
                      onClick={() => setFilterState({searchParams: ['featureName', '']})}
                    />
                    <B.MenuItem
                      text="Search observation text"
                      icon={filters.searchParams[0] === 'observationText' ? 'small-tick' : 'blank'}
                      onClick={() => setFilterState({searchParams: ['observationText', '']})}
                    />
                  </B.Menu>
                }
              >
                <B.Button icon="caret-down" minimal />
              </B.Popover>
            }
          />
        </div>
        <B.Popover
          position={B.Position.BOTTOM_LEFT}
          content={
            <div style={{padding: 8}}>
              <B.ButtonGroup>
                <B.Button
                  icon="form"
                  active={filters.status === 'new'}
                  onClick={() => setFilterState({status: filters.status === 'new' ? 'all' : 'new'})}
                  small
                  text="New"
                />
                <B.Button
                  icon={'tick'}
                  active={filters.status === 'saved'}
                  onClick={() =>
                    setFilterState({status: filters.status === 'saved' ? 'all' : 'saved'})
                  }
                  small
                  text="Saved"
                />
                <B.Button
                  icon={'cross'}
                  active={filters.status === 'archived'}
                  onClick={() =>
                    setFilterState({
                      status: filters.status === 'archived' ? 'all' : 'archived',
                    })
                  }
                  small
                  text="Archived"
                />
              </B.ButtonGroup>
            </div>
          }
        >
          <B.Button
            minimal
            icon={statusMeta.icon}
            text={statusMeta.text}
            active={filters.status !== 'all'}
          />
        </B.Popover>
        <B.Popover
          position={B.Position.BOTTOM_RIGHT}
          content={
            <B.Menu>
              <B.MenuItem
                text={
                  sortField === 'featureName' || sortField === 'observationText'
                    ? 'A-Z'
                    : 'Lowest to highest'
                }
                icon={sortDirection === 'asc' ? 'small-tick' : 'blank'}
                onClick={() => setFilterState({sortParams: [sortField, 'asc']})}
              />
              <B.MenuItem
                text={
                  sortField === 'featureName' || sortField === 'observationText'
                    ? 'Z-A'
                    : 'Highest to lowest'
                }
                icon={sortDirection === 'desc' ? 'small-tick' : 'blank'}
                onClick={() => setFilterState({sortParams: [sortField, 'desc']})}
              />
              <B.MenuDivider />
              <B.MenuItem
                text={'Property name'}
                icon={sortField === 'featureName' ? 'small-tick' : 'blank'}
                onClick={() => setFilterState({sortParams: ['featureName', sortDirection]})}
              />
              <B.MenuItem
                text={'Observation'}
                icon={sortField === 'observationText' ? 'small-tick' : 'blank'}
                onClick={() => setFilterState({sortParams: ['observationText', sortDirection]})}
              />
              <B.MenuItem
                text={'Property area'}
                icon={sortField === 'propertyArea' ? 'small-tick' : 'blank'}
                onClick={() => setFilterState({sortParams: ['propertyArea', sortDirection]})}
              />
              <B.MenuItem
                text={'Observation area'}
                icon={sortField === 'observationGeometryArea' ? 'small-tick' : 'blank'}
                onClick={() =>
                  setFilterState({sortParams: ['observationGeometryArea', sortDirection]})
                }
              />
              <B.MenuItem
                text={'Status'}
                icon={sortField === 'status' ? 'small-tick' : 'blank'}
                onClick={() => setFilterState({sortParams: ['status', sortDirection]})}
              />
            </B.Menu>
          }
        >
          <B.Button
            minimal
            icon={
              sortDirection === 'asc' ? 'sort-asc' : sortDirection === 'desc' ? 'sort-desc' : 'sort'
            }
            title="Sort"
          />
        </B.Popover>
        <B.Button icon="refresh" title="Reset filters" onClick={handleReset} minimal />
        <B.Button
          icon="download"
          title="Export CSV"
          onClick={handleExportCSV}
          disabled={!noteQueue?.length}
          minimal
        />
        <RunProgressBarStatus
          savedCount={savedCount}
          dismissedCount={dismissedCount}
          newCount={newCount}
        />
        <div className={cs.selectionInfo}>
          {selectedNotes.length > 0 && (
            <>
              <B.Button
                minimal
                icon="tick"
                intent={B.Intent.PRIMARY}
                text="Save selected"
                onClick={bulkSaveNotes}
              />
              <B.Button
                minimal
                icon="cross"
                intent={B.Intent.PRIMARY}
                text="Dismiss selected"
                onClick={bulkDismissNotes}
              />
            </>
          )}
          <span style={{}}>
            {selectedNotes.length > 0 ? (
              <span>
                Selected {selectedNotes.length} of {notes?.length} observations
                <span>
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      clearSelection();
                    }}
                  >
                    {' - '}clear selection
                  </a>
                </span>
              </span>
            ) : (
              <span>
                Showing {noteQueue.length} of {notes?.length || 0} observations
                {noteQueue.length > 0 && (
                  <span>
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        selectAllNotes();
                      }}
                    >
                      {' - '}select all
                    </a>
                  </span>
                )}
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
